import { useState, useCallback } from 'react';
import { Bot, User, AlertTriangle, Clock } from 'lucide-react';
import clsx from 'clsx';

export interface EditConflict {
  id: string;
  filePath: string;
  userContent: string;
  aiContent: string;
  timestamp: number;
  resolved: boolean;
}

export interface UserEdit {
  id: string;
  filePath: string;
  content: string;
  timestamp: number;
  applied: boolean;
  conflicted: boolean;
}

export interface ConcurrentEditingState {
  userEdits: UserEdit[];
  conflicts: EditConflict[];
  isAIGenerating: boolean;
  currentAIFile?: string;
}

export interface ConcurrentEditingProps {
  state: ConcurrentEditingState;
  onResolveConflict: (conflictId: string, resolution: 'user' | 'ai' | 'merge') => void;
  onApplyQueuedEdits: () => void;
  className?: string;
}

export function ConcurrentEditingIndicator({
  state,
  onResolveConflict,
  onApplyQueuedEdits,
  className
}: ConcurrentEditingProps) {
  const [showDetails, setShowDetails] = useState(false);

  const pendingEdits = state.userEdits.filter(edit => !edit.applied);
  const unresolvedConflicts = state.conflicts.filter(conflict => !conflict.resolved);

  if (!state.isAIGenerating && pendingEdits.length === 0 && unresolvedConflicts.length === 0) {
    return null;
  }

  return (
    <div className={clsx('bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Bot className="size-4 text-blue-500" />
            {state.isAIGenerating && (
              <div className="size-2 bg-green-500 rounded-full animate-pulse" />
            )}
          </div>
          <span className="text-sm font-medium">
            Concurrent Editing
          </span>
        </div>
        
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          {showDetails ? 'Hide' : 'Show'} Details
        </button>
      </div>

      {/* Status Summary */}
      <div className="p-3 space-y-2">
        {state.isAIGenerating && state.currentAIFile && (
          <div className="flex items-center gap-2 text-sm">
            <Bot className="size-4 text-blue-500" />
            <span className="text-gray-600 dark:text-gray-400">
              AI editing: <span className="font-medium">{state.currentAIFile.split('/').pop()}</span>
            </span>
          </div>
        )}

        {pendingEdits.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="size-4 text-orange-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {pendingEdits.length} pending edit{pendingEdits.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={onApplyQueuedEdits}
              className="ml-auto px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
            >
              Apply All
            </button>
          </div>
        )}

        {unresolvedConflicts.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="size-4 text-red-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {unresolvedConflicts.length} conflict{unresolvedConflicts.length !== 1 ? 's' : ''} need resolution
            </span>
          </div>
        )}
      </div>

      {/* Detailed View */}
      {showDetails && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Pending Edits */}
          {pendingEdits.length > 0 && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <User className="size-4" />
                Pending User Edits
              </h4>
              <div className="space-y-2">
                {pendingEdits.map(edit => (
                  <div key={edit.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="flex-1">
                      <div className="text-sm font-medium">{edit.filePath.split('/').pop()}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(edit.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {edit.conflicted && (
                        <AlertTriangle className="size-4 text-red-500" />
                      )}
                      <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded dark:bg-orange-900/30 dark:text-orange-400">
                        Queued
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conflicts */}
          {unresolvedConflicts.length > 0 && (
            <div className="p-3">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <AlertTriangle className="size-4 text-red-500" />
                Edit Conflicts
              </h4>
              <div className="space-y-3">
                {unresolvedConflicts.map(conflict => (
                  <div key={conflict.id} className="p-3 border border-red-200 dark:border-red-700 rounded">
                    <div className="text-sm font-medium mb-2">{conflict.filePath.split('/').pop()}</div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                        <div className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">AI Changes</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                          {conflict.aiContent.substring(0, 100)}...
                        </div>
                      </div>
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                        <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">Your Changes</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                          {conflict.userContent.substring(0, 100)}...
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onResolveConflict(conflict.id, 'ai')}
                        className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400"
                      >
                        Keep AI
                      </button>
                      <button
                        onClick={() => onResolveConflict(conflict.id, 'user')}
                        className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400"
                      >
                        Keep Mine
                      </button>
                      <button
                        onClick={() => onResolveConflict(conflict.id, 'merge')}
                        className="px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-400"
                      >
                        Merge
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Hook for managing concurrent editing state
 */
export function useConcurrentEditing() {
  const [state, setState] = useState<ConcurrentEditingState>({
    userEdits: [],
    conflicts: [],
    isAIGenerating: false,
    currentAIFile: undefined
  });

  // Add a user edit to the queue
  const addUserEdit = useCallback((filePath: string, content: string) => {
    const edit: UserEdit = {
      id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      filePath,
      content,
      timestamp: Date.now(),
      applied: false,
      conflicted: false
    };

    setState(prev => ({
      ...prev,
      userEdits: [...prev.userEdits, edit]
    }));

    return edit.id;
  }, []);

  // Update AI generation status
  const updateAIStatus = useCallback((isGenerating: boolean, currentFile?: string) => {
    setState(prev => ({
      ...prev,
      isAIGenerating: isGenerating,
      currentAIFile: currentFile
    }));
  }, []);

  // Create a conflict when AI and user edits overlap
  const createConflict = useCallback((filePath: string, userContent: string, aiContent: string) => {
    const conflict: EditConflict = {
      id: `conflict-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      filePath,
      userContent,
      aiContent,
      timestamp: Date.now(),
      resolved: false
    };

    setState(prev => ({
      ...prev,
      conflicts: [...prev.conflicts, conflict]
    }));

    return conflict.id;
  }, []);

  // Resolve a conflict
  const resolveConflict = useCallback((conflictId: string, _resolution: 'user' | 'ai' | 'merge') => {
    setState(prev => ({
      ...prev,
      conflicts: prev.conflicts.map(conflict =>
        conflict.id === conflictId
          ? { ...conflict, resolved: true }
          : conflict
      )
    }));
  }, []);

  // Apply all queued edits
  const applyQueuedEdits = useCallback(() => {
    setState(prev => ({
      ...prev,
      userEdits: prev.userEdits.map(edit => ({ ...edit, applied: true }))
    }));
  }, []);

  // Clear applied edits and resolved conflicts
  const cleanup = useCallback(() => {
    setState(prev => ({
      ...prev,
      userEdits: prev.userEdits.filter(edit => !edit.applied),
      conflicts: prev.conflicts.filter(conflict => !conflict.resolved)
    }));
  }, []);

  return {
    state,
    addUserEdit,
    updateAIStatus,
    createConflict,
    resolveConflict,
    applyQueuedEdits,
    cleanup
  };
}

/**
 * Visual indicator for AI vs User content
 */
export interface ContentOriginIndicatorProps {
  origin: 'ai' | 'user' | 'mixed';
  className?: string;
}

export function ContentOriginIndicator({ origin, className }: ContentOriginIndicatorProps) {
  const config = {
    ai: {
      icon: Bot,
      color: 'text-blue-500',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      label: 'AI Generated'
    },
    user: {
      icon: User,
      color: 'text-green-500',
      bg: 'bg-green-50 dark:bg-green-900/20',
      label: 'User Edited'
    },
    mixed: {
      icon: AlertTriangle,
      color: 'text-orange-500',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      label: 'Mixed Content'
    }
  };

  const { icon: Icon, color, bg, label } = config[origin];

  return (
    <div className={clsx('flex items-center gap-1 px-2 py-1 rounded text-xs', bg, className)}>
      <Icon className={clsx('size-3', color)} />
      <span className={color}>{label}</span>
    </div>
  );
}
